import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import { getSearchResults } from "../api";
import {
  getSelectedPropertyExpediaHotelIds,
  getSelectedPropertyObjects,
  convertISODateToYMDRespectingUserTimezone,
  convertUserSelectedDates,
  getAgeString,
  DEFAULT_PAGE_SIZE,
} from "../pages/price-check/utils";
import {
  persistFormValues,
  persistSearchState,
  getInitialSearchState,
  getInitialFormValues,
  formatSearchErrorMessage,
  getPastCheckInConflicts,
  getPastCheckInMessages,
  getPastBookDateConflicts,
  getPastBookDateMessages,
} from "../components/priceCheck-form/PriceCheckFormUtils";
import {
  persistFilterValues,
  getInitialFilterValues,
} from "../components/priceCheck-form/PriceCheckFilterUtils";
import {
  processFiltersForAPI,
  convertFilterOrderToAPI,
} from "../utils/filterUtils";

// Default state constants
const DEFAULT_PRICE_CHECK_PAGE = {
  size: DEFAULT_PAGE_SIZE,
  number: 0,
  totalElements: 0,
  totalPages: 1,
};

const DEFAULT_PRICE_CHECK_DATA = [];

const DEFAULT_FILTERS_DATA = {
  ratePlans: [],
  roomNames: [],
  mealPlans: [],
  promotions: [],
  refundableOptions: [],
};

// Export default constants for use in other components
export {
  DEFAULT_PRICE_CHECK_PAGE,
  DEFAULT_PRICE_CHECK_DATA,
  DEFAULT_FILTERS_DATA,
};

export const usePriceCheckSearch = ({
  initialEdit,
  initialPriceCheckData,
  properties,
}) => {
  // Initialize search state from localStorage
  const initialSearchState = getInitialSearchState(initialEdit);
  const [isEdit, setIsEdit] = useState(initialSearchState.isEdit);
  const [hasSearchResults, setHasSearchResults] = useState(
    initialSearchState.hasSearchResults
  );

  const [isLoading, setIsLoading] = useState(false);
  const [priceCheckData, setPriceCheckData] = useState(
    initialPriceCheckData || DEFAULT_PRICE_CHECK_DATA
  );
  const [priceCheckPage, setPriceCheckPage] = useState(
    DEFAULT_PRICE_CHECK_PAGE
  );
  const [sortState, setSortState] = useState({
    sortBy: null,
    sortDirection: null,
  });
  const [filtersData, setFiltersData] = useState(DEFAULT_FILTERS_DATA);

  // Initialize form values from localStorage
  const [formValues, setFormValues] = useState(() => {
    const savedFormValues = getInitialFormValues(properties);
    return (
      savedFormValues || {
        selectedProperties: [],
        selectedCity: [],
        dateRanges: [],
        adults: 2,
        children: 0,
      }
    );
  });

  // Initialize filter state from localStorage
  const initialFilterValues = getInitialFilterValues();
  const [selectedFilters, setSelectedFilters] = useState(
    initialFilterValues.selectedFilters
  );
  const [filterSelectionOrder, setFilterSelectionOrder] = useState(
    initialFilterValues.filterSelectionOrder
  );

  const [searchCounter, setSearchCounter] = useState(0);
  const [hasAutoSearched, setHasAutoSearched] = useState(false);
  const [skipFilterPersistence, setSkipFilterPersistence] = useState(false);
  const handleSearchRef = useRef();
  const autoSearchInitiatedRef = useRef(false);

  // Helper function to determine which filters to use for the search
  const determineFiltersToUse = useCallback(
    (searchParams, overrideFilters, isAutoSearch, currentSelectedFilters) => {
      const shouldResetFilters =
        !searchParams?.page &&
        !searchParams?.sortBy &&
        !overrideFilters &&
        !isAutoSearch;

      if (shouldResetFilters) {
        const resetFilters = {
          ratePlans: [],
          roomNames: [],
          promotions: [],
          mealPlans: [],
          refundableOptions: [],
        };
        setSelectedFilters(resetFilters);
        setSearchCounter((prev) => prev + 1);
        return resetFilters;
      }

      if (overrideFilters) {
        return overrideFilters;
      }

      return processFiltersForAPI(currentSelectedFilters);
    },
    []
  );

  // Helper function to handle successful API response
  const handleSuccessResponse = useCallback((data) => {
    setPriceCheckData(data.roomRates);
    setPriceCheckPage((prev) => ({
      ...prev,
      totalElements: data.totalElements,
      totalPages: data.totalPages,
      page: data.page,
    }));

    if (data.filtersResponse) {
      setFiltersData(data.filtersResponse);
    }

    setHasSearchResults(true);
  }, []);

  // Helper function to reset state to default values after API failure
  const resetToDefaultState = useCallback(() => {
    setPriceCheckData(DEFAULT_PRICE_CHECK_DATA);
    setPriceCheckPage(DEFAULT_PRICE_CHECK_PAGE);
    setFiltersData(DEFAULT_FILTERS_DATA);
    setHasSearchResults(false);
  }, []);

  // Main search function
  const handleSearch = useCallback(
    async (
      values,
      searchParams,
      overrideFilters = null,
      passedFilterSelectionOrder = null,
      isAutoSearch = false,
      specificHotelIds = null
    ) => {
      // Only update formValues if this is not an auto-search to prevent circular updates
      if (!isAutoSearch) {
        setFormValues(values);
      }
      persistFormValues(values);

      // Determine which filters to use
      const filtersToUse = determineFiltersToUse(
        searchParams,
        overrideFilters,
        isAutoSearch,
        selectedFilters
      );

      // Use the passed filterSelectionOrder or convert UI selection order to API format
      const currentFilterSelectionOrder =
        passedFilterSelectionOrder ||
        convertFilterOrderToAPI(filterSelectionOrder);

      // Persist filter values if there are any
      if (currentFilterSelectionOrder?.length > 0) {
        persistFilterValues(filtersToUse, currentFilterSelectionOrder);
      }

      // EO-1137 — last gate before the request, on purpose.
      //
      // The picker already blocks past dates, but it cannot cover this: dates are
      // chosen before properties are, and usePriceCheckAutoSearch replays a saved
      // search on a later day without passing through the form at all. Both reach
      // here, so here is the only place that catches every route.
      //
      // Per property, because "past" is not one question: a check-in of the 17th is
      // fine in India and already gone in Japan. Naming the property and the date
      // matters when several are selected — a bare "check-in is in the past" leaves
      // the user guessing which hotel to fix.
      //
      // Skips silently when no property carries a timeZone, which is the case until
      // the properties API ships one. That keeps it from ever blocking a search it
      // cannot actually judge.
      //
      // ⚠ Deliberately OUTSIDE the try below, and before setIsLoading. Inside it, a
      // fault in this guard was indistinguishable from a failed search: the catch
      // turned it into a "Failed to fetch data" toast and swallowed the real cause.
      // Out here it fails loudly, and no loading state has been entered yet, so an
      // early return cannot strand the page mid-spin.
      const selectedPropertyObjects = getSelectedPropertyObjects(
        properties,
        values?.selectedCity || [],
        values?.selectedProperties || []
      );
      const zonedProperties = (selectedPropertyObjects || []).filter(
        (p) => p?.timeZone
      );
      const pastCheckInConflicts = getPastCheckInConflicts(
        zonedProperties,
        values?.dateRanges || []
      );
      // Same check for the booking creation date, which reaches the API from these
      // same paths and is judged against the user's clock everywhere else.
      const pastBookDateConflicts = getPastBookDateConflicts(
        zonedProperties,
        values?.bookDate
      );
      if (pastCheckInConflicts.length > 0 || pastBookDateConflicts.length > 0) {
        [
          ...getPastCheckInMessages(pastCheckInConflicts),
          ...getPastBookDateMessages(pastBookDateConflicts),
        ].forEach((message) => toast.error(message));
        return;
      }

      setIsEdit(false);
      setIsLoading(true);

      try {
        // Use specific hotel IDs for tab switching, otherwise use all selected hotel IDs
        const hotelIds =
          specificHotelIds ||
          getSelectedPropertyExpediaHotelIds(
            properties,
            values?.selectedCity || [],
            values?.selectedProperties || []
          );

        const response = await getSearchResults({
          hotelIds,
          dates: convertUserSelectedDates(values?.dateRanges || []),
          bookDate: values?.bookDate
            ? convertISODateToYMDRespectingUserTimezone(
                new Date(values?.bookDate)
              )
            : undefined,
          guestAges: getAgeString(values?.adults, values?.childAges),
          page: searchParams?.page || 1,
          pageSize: searchParams?.pageSize || DEFAULT_PAGE_SIZE,
          sortBy: searchParams?.sortBy || sortState.sortBy,
          sortDirection: searchParams?.sortDirection || sortState.sortDirection,
          filters: filtersToUse,
          filterStack: currentFilterSelectionOrder,
        });

        if (response?.status === 200) {
          handleSuccessResponse(response.data);
        } else {
          // EO-1137: strip the "Occupancy Node [1]: " style prefix before showing it. The blanket
          // message stays as the fallback, for a failure that carries nothing worth showing.
          toast.error(
            formatSearchErrorMessage(
              response?.data?.error || response?.message,
              "Failed to fetch data"
            )
          );
          // Reset to default state after API failure
          resetToDefaultState();
        }
      } catch (error) {
        toast.error(
          formatSearchErrorMessage(error?.message, "Failed to fetch data")
        );
        // Reset to default state after API failure
        resetToDefaultState();
      } finally {
        setIsLoading(false);
      }
    },
    [
      properties,
      sortState.sortBy,
      sortState.sortDirection,
      selectedFilters,
      filterSelectionOrder,
      determineFiltersToUse,
      handleSuccessResponse,
      resetToDefaultState,
    ]
  );

  // Update the ref to always point to the latest handleSearch
  handleSearchRef.current = handleSearch;

  // Persist search state whenever it changes
  useEffect(() => {
    persistSearchState(isEdit, hasSearchResults);
  }, [isEdit, hasSearchResults]);

  // Persist filter values whenever they change (unless skipping persistence)
  useEffect(() => {
    if (!skipFilterPersistence) {
      persistFilterValues(selectedFilters, filterSelectionOrder);
    }
  }, [selectedFilters, filterSelectionOrder, skipFilterPersistence]);

  return {
    // State
    isEdit,
    setIsEdit,
    hasSearchResults,
    setHasSearchResults,
    isLoading,
    priceCheckData,
    priceCheckPage,
    setPriceCheckPage,
    sortState,
    setSortState,
    filtersData,
    formValues,
    setFormValues,
    selectedFilters,
    setSelectedFilters,
    filterSelectionOrder,
    setFilterSelectionOrder,
    searchCounter,
    setSearchCounter,
    setSkipFilterPersistence,
    // Functions
    handleSearch,
    handleSearchRef,
    // Auto-search refs
    hasAutoSearched,
    setHasAutoSearched,
    autoSearchInitiatedRef,
  };
};
